import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { injectIntl } from "react-intl";
import Select from "react-select";
import { Loader, Card, CardSection } from "cxa-components";
import ClaimStepper from "./ClaimStepper";
import {
  getClaimCategories,
  getClaimAccounts,
  getDelegatedEmployees,
  setSelectedClaimant,
  setSelectedPlanYear,
  getClaimsNeedHelp,
  setSelectedCategory,
  setSelectedClaim,
} from "../../actions/claimActionsPP";
import AccountAccordion from "./widgets/AccountAccordion";
import PointsToConsider from "./widgets/PointsToConsider";
import SelectClaim from "./SelectClaim";
import messages from "components/messagesDescription";
import { getShortLivedToken } from "actions/loginActions";
import { Prompt } from "react-router-dom";
import PropTypes from "prop-types";

const useClaimData = data => {
  const planYears = data.planYears.map(item => ({ value: item.planID, label: item.planName }));
  const claimants = data.claimants.map(item => ({
    value: `${item.type}|${item.claimantKey}`,
    label: item.fullName,
  }));
  const showClaimsAsList = data.showClaimsAsList;
  const selectedClaim = data.selectedClaim || null;
  const selectedCategory = data.selectedCategory || null;

  let selectedAccounts = [];
  if (selectedClaim && selectedClaim.accounts) {
    const selectedClaimant = data.selectedClaimant || null;
    const selectedClaimantId = selectedClaimant && Number(selectedClaimant.split("|")[1]);
    const selectedClaimantType = selectedClaimant && selectedClaimant.split("|")[0];
    if (selectedClaimantId && selectedClaimantType) {
      const filteredAccounts = data.claimAccounts.filter(item =>
        selectedClaim.accounts.find(x => item.accountName === x)
      );
      selectedAccounts = filteredAccounts.filter(
        acc => acc.memberKey === selectedClaimantId && acc.memberType === selectedClaimantType
      );
      if (selectedAccounts.length === 0) {
        selectedAccounts = filteredAccounts.filter(acc => acc.memberType === "Employee");
      }
    }
  }

  const filteredDelegatorData = data.delegatedEmployees.filter(item => item.role !== "Manager");

  const delegators = filteredDelegatorData.map(item => ({
    value: `${item.employeeKey || null}`,
    label: item.delegatedEmployeeName,
  }));

  return {
    planYears,
    claimants,
    selectedClaim,
    selectedCategory,
    selectedAccounts,
    delegators,
    showClaimsAsList,
  };
};

const getClaimClassificationsByClaimant = (claimClassifications, claimantId) => {
  if (!claimantId) return claimClassifications;

  return claimClassifications
    .map(cla => {
      const filteredCategories = cla.claimCategories
        .map(cat => ({
          ...cat,
          children: cat.children.filter(child => child.claimants.includes(claimantId)),
        }))
        .filter(cat => cat.children.length > 0); // Keep only categories with matching children

      return filteredCategories.length > 0 ? { ...cla, claimCategories: filteredCategories } : null; // Null if no matching categories
    })
    .filter(Boolean); // Remove null entries
};

const getActivePlanYear = (selectedPlanYear, planYears) => {
  return selectedPlanYear || planYears.find(item => item.isSelected).planID;
};

const renderPlanYears = ({ props, activePlanYear, onPlanYearChanged, planYears }) => (
  <CardSection style={{ width: "180px" }}>
    <h5> {props.intl.formatMessage(messages.claims.planYears)}</h5>
    <Select
      value={activePlanYear}
      defaultValue={activePlanYear}
      onChange={onPlanYearChanged}
      isClearable={false}
      options={planYears}
    ></Select>
  </CardSection>
);

const renderDelegators = ({ props, delegators, delegator, onDelegatorChanged }) => (
  <CardSection>
    <h5> {props.intl.formatMessage(messages.claims.submitClaimFor)}</h5>
    <Select
      options={delegators}
      isClearable={false}
      value={delegator}
      onChange={onDelegatorChanged}
    ></Select>
    <span style={{ fontSize: "0.75rem", paddingLeft: "10px" }}>
      {props.intl.formatMessage(messages.claims.submitClaimForInstruction)}
    </span>
  </CardSection>
);

const renderClaimants = ({ props, claimants, activeClaimant, onClaimantChanged }) => (
  <CardSection>
    <h5> {props.intl.formatMessage(messages.claims.claimants)}</h5>
    <Select
      options={claimants}
      isClearable={false}
      value={activeClaimant}
      onChange={onClaimantChanged}
    ></Select>
  </CardSection>
);

const renderSelectClaim = ({
  claimClassifications,
  selectedCategory,
  selectedClaim,
  activePlanYear,
  delegator,
  activeClaimantId,
  dispatch,
  setDirty,
  showClaimsAsList,
  props,
}) => (
  <SelectClaim
    claimClassifications={claimClassifications}
    selectedCategory={selectedCategory}
    selectedClaim={selectedClaim}
    activePlanYear={activePlanYear}
    setSelectedClaim={setSelectedClaim}
    setSelectedCategory={setSelectedCategory}
    selectedDelegator={delegator !== "me" ? delegator : null}
    activeClaimant={activeClaimantId}
    getShortLivedToken={getShortLivedToken(dispatch)}
    setDirty={setDirty}
    showClaimsAsList={showClaimsAsList}
    {...props}
  />
);

const renderAccountAccordion = (acc, props) => (
  <AccountAccordion
    key={`${acc.accountName}_${acc.memberKey}_${acc.memberType}`}
    data={acc}
    {...props}
  ></AccountAccordion>
);

const renderAccountBalance = ({ selectedAccounts, props }) => (
  <CardSection>
    <div style={{ textAlign: "center" }}>
      {selectedAccounts && selectedAccounts.length > 0 ? (
        <h3> {props.intl.formatMessage(messages.claims.yourAccountBalance)}</h3>
      ) : (
        <div style={{ minHeight: "65px" }}>
          <h3> {props.intl.formatMessage(messages.noAccountsDescription)}</h3>
        </div>
      )}
    </div>
    {selectedAccounts.map(acc => acc && renderAccountAccordion(acc, props))}
  </CardSection>
);

const renderClaimInformation = ({ data, props }) => (
  <CardSection>
    <div style={{ textAlign: "center" }}>
      {data.claimAccounts && data.claimAccounts.length > 0 ? (
        <h3> {props.intl.formatMessage(messages.claims.yourAccountBalance)}</h3>
      ) : (
        <div style={{ minHeight: "65px" }}>
          <h3> {props.intl.formatMessage(messages.noAccountsDescription)}</h3>
        </div>
      )}
    </div>
    {data.claimAccounts.map(acc => acc && renderAccountAccordion(acc, props))}
  </CardSection>
);

const renderPointsToConsider = ({ selectedClaim, props }) => {
  return <PointsToConsider selectedClaim={selectedClaim} props={props} />;
};

const SelectClaimPage = props => {
  const [delegator, setDelegator] = useState("me");
  const [isDirty, setDirty] = useState(false);

  const dispatch = useDispatch();
  const data = useSelector(state => state.claimsPP);

  useEffect(() => {
    dispatch(getClaimCategories(data.selectedPlanYear));
    dispatch(getClaimAccounts(data.selectedPlanYear));
    dispatch(getDelegatedEmployees());
    dispatch(getClaimsNeedHelp());
    //Scroll to the top of the page
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const {
    planYears,
    claimants,
    selectedClaim,
    selectedCategory,
    selectedAccounts,
    delegators,
    showClaimsAsList,
  } = useClaimData(data);

  if (data.isFetching || data.planYears.length <= 0 || data.claimants.length <= 0)
    return <Loader />;

  const activeClaimant = data.selectedClaimant || claimants[0].value || "";
  const activeClaimantId = activeClaimant
    ? Number(activeClaimant && activeClaimant.split("|")[1])
    : "";

  const claimClassifications = getClaimClassificationsByClaimant(
    data.claimClassifications,
    activeClaimantId
  );
  const activePlanYear = getActivePlanYear(data.selectedPlanYear, data.planYears);

  const onPlanYearChanged = data => {
    setDelegator("me");
    setDirty(true);
    dispatch(setSelectedPlanYear(data.value));
    dispatch(getClaimCategories(data.value));
    dispatch(getClaimAccounts(data.value));
  };

  const onClaimantChanged = data => {
    dispatch(setSelectedClaimant(data.value));
    setSelectedCategory(null);
    setSelectedClaim(null);
    setDirty(true);
  };

  const onDelegatorChanged = data => {
    setDelegator(data.value);
    if (data.value !== "me") {
      dispatch(getClaimCategories(activePlanYear, data.value));
      dispatch(getClaimAccounts(activePlanYear, null, data.value));
      // dispatch(setSelectedClaimant(""));
    } else {
      dispatch(getClaimCategories(activePlanYear));
      dispatch(getClaimAccounts(activePlanYear));
    }
    setSelectedCategory(null);
    setSelectedClaim(null);
    setDirty(true);
  };

  return (
    <Fragment>
      <ClaimStepper index={0} />
      <h3>
        <span className="claim-header">
          {props.intl.formatMessage(messages.claims.selectClaimType)}
        </span>
      </h3>
      <div className="csp-side-menu claim-container-layout">
        <div className="cxa-grid__item--nine">
          <Card>
            {renderPlanYears({ props, activePlanYear, onPlanYearChanged, planYears })}
            {delegators &&
              delegators.length > 1 &&
              renderDelegators({ props, delegators, delegator, onDelegatorChanged })}
            <Fragment>
              {renderClaimants({ props, claimants, activeClaimant, onClaimantChanged })}
              {renderSelectClaim({
                claimClassifications,
                selectedCategory,
                selectedClaim,
                activePlanYear,
                delegator,
                activeClaimantId,
                dispatch,
                setDirty,
                showClaimsAsList,
                props,
              })}
            </Fragment>
          </Card>
        </div>
        <div className="cxa-grid__item--three claim-panel-mobile-container">
          {selectedClaim ? (
            <Fragment>
              <div className="claim-panel-mobile">
                <Card>{renderAccountBalance({ selectedAccounts, props })}</Card>
              </div>
              <Card>{renderPointsToConsider({ selectedClaim, props })}</Card>
            </Fragment>
          ) : (
            <div className="claim-panel-mobile">
              <Card>{renderClaimInformation({ data, props })}</Card>
            </div>
          )}
        </div>
        <Prompt
          when={isDirty}
          message={props.intl.formatMessage(messages.claims.saveClaimInformationDescription)}
        />
      </div>
    </Fragment>
  );
};

SelectClaimPage.propTypes = {
  intl: PropTypes.object,
};

export default injectIntl(SelectClaimPage);



// WEBPACK FOOTER //
// ./components/claim/SelectClaimPage.js